import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as ep from '../api/endpoints';
import type { DuplicateCase, Period, SetupCode } from '../api/types';
import { useMe } from '../app/auth';
import { usePeriods } from '../components/PeriodPicker';
import { Empty, ErrorBox, Field, Loading, Money, PeriodBadge } from '../components/ui';
import { formatDateTime, yearMonthLabel } from '../lib/dates';

/**
 * Yönetim (PDF §04/§05/§08): kullanıcı tanımlama (Gmail), şifre sıfırlama, yetki, erişim kapatma, gider kalemleri,
 * mükerrer incelemesi, ay sonu kapanışı, bildirimler. Yalnızca yöneticiye görünür; sunucu da her işlemde yetki denetler.
 * Yönetici başkasının fişini değiştiremez; mükerrer kararı ve kalem birleştirme fiş alanlarını değiştirmez.
 */
export function AdminScreen() {
  const [tab, setTab] = useState<'users' | 'close' | 'cats' | 'dups' | 'notif'>('users');
  return (
    <div>
      <h1>Yönetim</h1>
      <p className="small muted" style={{ marginTop: -6 }}>Bu bölüm yalnızca yöneticilere görünür.</p>
      <div className="tabs" role="tablist">
        {([['users', 'Kullanıcılar'], ['close', 'Ay kapanışı'], ['cats', 'Gider kalemleri'], ['dups', 'Mükerrer inceleme'], ['notif', 'Bildirimler']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      {tab === 'users' && <UsersAdmin />}
      {tab === 'close' && <PeriodClose />}
      {tab === 'cats' && <CategoriesAdmin />}
      {tab === 'dups' && <Duplicates />}
      {tab === 'notif' && <Notifications />}
    </div>
  );
}

function PeriodClose() {
  const periods = usePeriods();
  const open = periods.data?.filter((p) => p.status === 'OPEN') ?? [];
  return (
    <div className="stack">
      {!periods.data ? <Loading /> : open.map((p) => <PeriodCloseCard key={p.id} p={p} />)}
      <h2>Kapanmış aylar</h2>
      <div className="card">
        <ul className="list">
          {periods.data?.filter((p) => p.status === 'CLOSED').map((p) => (
            <li key={p.id}><div className="item"><span style={{ flex: 1 }}>{yearMonthLabel(p.year_month)}</span><PeriodBadge status="CLOSED" /><span className="small muted">{formatDateTime(p.closed_at)}</span></div></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function PeriodCloseCard({ p }: { p: Period }) {
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['period', p.id], queryFn: () => ep.period(p.id) });
  const dash = useQuery({ queryKey: ['dashboard', p.id], queryFn: () => ep.dashboard(p.id) });
  const [asking, setAsking] = useState(false);
  const close = useMutation({
    mutationFn: () => ep.closePeriod(p.id),
    onSettled: () => setAsking(false),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['periods'] }), qc.invalidateQueries({ queryKey: ['period', p.id] }), qc.invalidateQueries({ queryKey: ['dashboard'] })]),
  });
  const pending = detail.data?.pending;
  const ready = p.month_ended && pending?.count === 0;
  const monthName = yearMonthLabel(p.year_month);
  return (
    <div className="card stack">
      <div className="row"><h3 style={{ margin: 0 }}>{yearMonthLabel(p.year_month)}</h3><PeriodBadge status={p.status} /></div>
      <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
        <li>{p.month_ended ? '✔ Ay sonu geldi; kapatılabilir' : '✖ Ay henüz bitmedi; ayın son günü saat 18:00\'den itibaren kapatılabilir'}</li>
        <li>{pending ? (pending.count ? `✖ ${pending.count} bekleyen yükleme/OCR/inceleme var` : '✔ Bekleyen yükleme, OCR veya inceleme yok') : '…'}</li>
        {dash.data && <li>Net toplam <Money value={dash.data.totals.net_kurus} /> · {dash.data.counts.active} aktif fiş · {dash.data.counts.cancelled} iptal · {dash.data.counts.refunds} iade</li>}
        <li>Sunucu, çevrimdışı cihazlardaki taslakları bilemez. Kapanıştan sonra bu aya ait taslaklar reddedilir.</li>
      </ul>
      {pending && pending.count > 0 && (
        <div className="small">Bekleyenler: {pending.items.map((i) => `${i.status} (${i.document_date ?? 'tarih yok'})`).join(', ')}</div>
      )}
      {ready && (
        <>
          <ErrorBox error={close.error} />
          <div><button className="btn primary" disabled={close.isPending} onClick={() => setAsking(true)}>Ayı kapat</button></div>
        </>
      )}
      {asking && (
        <div className="confirm-overlay" role="dialog" aria-modal="true" aria-labelledby={`close-title-${p.id}`}
          onClick={() => { if (!close.isPending) setAsking(false); }}>
          <div className="confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 id={`close-title-${p.id}`}>{monthName} ayını kapatıyorsunuz</h3>
            <p>Kapatmanız durumunda <strong>{monthName}</strong> ayı üzerinde hiçbir değişiklik yapamayacaksınız:
              bu aya fiş eklenemez, fişler düzeltilemez veya iptal edilemez ve ay yeniden açılamaz.</p>
            <p><strong>Yine de kapatmak istiyor musunuz?</strong></p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="btn" disabled={close.isPending} onClick={() => setAsking(false)} autoFocus>Vazgeç</button>
              <button className="btn primary" disabled={close.isPending} onClick={() => close.mutate()}>
                {close.isPending ? 'Kapatılıyor…' : 'Evet, ayı kapat'}
              </button>
            </div>
          </div>
        </div>
      )}
      {close.data && <div className="alert ok">Ay kapatıldı. Kapanış özeti: {close.data.content_hash.slice(0, 12)}…</div>}
    </div>
  );
}

function Duplicates() {
  const q = useQuery({ queryKey: ['dup-cases'], queryFn: ep.duplicateCases });
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading />;
  if (!q.data.length) return <Empty>İnceleme bekleyen mükerrer şüphesi yok.</Empty>;
  return <div className="stack">{q.data.map((c) => <DuplicateCard key={c.id} c={c} />)}</div>;
}

function DuplicateCard({ c }: { c: DuplicateCase }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: (decision: DuplicateCase['decision']) => ep.decideDuplicate(c.id, decision, reason, c.candidate_version),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dup-cases'] }),
  });
  return (
    <div className="card stack">
      <div className="row"><strong>{c.level === 'STRONG' ? 'Güçlü şüphe' : 'Yetersiz bilgi'}</strong><span className="badge warn">İncelemede</span><span className="spacer" /><span className="small muted">{formatDateTime(c.created_at)}</span></div>
      <div className="small">Eşleşen kayıt: <Link to={`/fis/${c.matched_receipt_id}`}>{c.matched_receipt_id.slice(0, 8)}</Link></div>
      <div className="small muted">Kanıt: {Object.entries(c.evidence).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}</div>
      <Field label="Gerekçe (zorunlu)"><input value={reason} onChange={(e) => setReason(e.target.value)} minLength={3} /></Field>
      <ErrorBox error={m.error} />
      <div className="row">
        <button className="btn" disabled={reason.length < 3 || m.isPending} onClick={() => m.mutate('SEPARATE')}>Ayrı belge</button>
        <button className="btn danger" disabled={reason.length < 3 || m.isPending} onClick={() => m.mutate('DUPLICATE')}>Mükerrer</button>
        <button className="btn" disabled={reason.length < 3 || m.isPending} onClick={() => m.mutate('NEED_BETTER_IMAGE')}>Daha net görsel iste</button>
      </div>
      <p className="small muted">Karar yalnızca incelenen sürüm için geçerlidir; fiş sahibi görsel veya alanları değiştirirse yeniden incelenir. Kesin aynı fiş için "zorla ekle" yoktur.</p>
    </div>
  );
}

/** Kullanıcılar: yönetici Gmail adresiyle tanımlar; kullanıcı ilk girişte kodla kendi şifresini belirler. */
function UsersAdmin() {
  const me = useMe();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['members'], queryFn: ep.members });
  const [form, setForm] = useState({ email: '', full_name: '', role: 'member' as 'member' | 'admin' });
  const [issued, setIssued] = useState<SetupCode & { kind: 'new' | 'reset' } | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['members'] });
  const create = useMutation({
    mutationFn: () => ep.createMember(form),
    onSuccess: (r) => { setIssued({ ...r, kind: 'new' }); setForm({ email: '', full_name: '', role: 'member' }); void refresh(); },
  });
  const reset = useMutation({ mutationFn: ep.resetMemberPassword, onSuccess: (r) => { setIssued({ ...r, kind: 'reset' }); void refresh(); } });
  const role = useMutation({ mutationFn: (v: { id: string; role: 'member' | 'admin' }) => ep.setMemberRole(v.id, v.role), onSuccess: refresh });
  const deact = useMutation({ mutationFn: ep.deactivateMember, onSuccess: refresh });
  const react = useMutation({ mutationFn: ep.reactivateMember, onSuccess: refresh });
  const set = (k: 'email' | 'full_name') => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="stack">
      {issued && <CodePanel code={issued} onClose={() => setIssued(null)} />}
      <form className="card stack" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <h3 style={{ margin: 0 }}>Kullanıcı tanımla</h3>
        <div className="form-grid two">
          <Field label="Gmail adresi"><input type="email" autoCapitalize="none" placeholder="ad.soyad@gmail.com" value={form.email} onChange={set('email')} required /></Field>
          <Field label="Ad soyad"><input value={form.full_name} onChange={set('full_name')} required minLength={3} maxLength={120} /></Field>
          <Field label="Yetki">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'member' | 'admin' })}>
              <option value="member">Kullanıcı (fiş ekler, ortak giderleri görür)</option>
              <option value="admin">Yönetici (bu paneli de görür)</option>
            </select>
          </Field>
        </div>
        <ErrorBox error={create.error} />
        <button className="btn primary" disabled={create.isPending}>{create.isPending ? 'Tanımlanıyor…' : 'Kullanıcıyı tanımla'}</button>
        <p className="small muted" style={{ margin: 0 }}>Sistem 6 haneli tek kullanımlık bir kod üretir. Kod kullanıcının Gmail adresine gönderilir ve size de bir kez gösterilir. Kullanıcı ilk girişte bu kodla kendi şifresini belirler.</p>
      </form>

      <h3>Kullanıcılar</h3>
      <ErrorBox error={reset.error ?? role.error ?? deact.error ?? react.error} />
      <div className="card"><ul className="list">
        {!members.data && <li><Loading lines={2} /></li>}
        {members.data?.map((m) => (
          <li key={m.user_id}><div className="item" style={{ flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div className="title">{m.full_name} {m.user_id === me.user.id && <span className="small muted">(siz)</span>}</div>
              <div className="small muted" style={{ overflowWrap: 'anywhere' }}>{m.email ?? m.username} · {m.role === 'admin' ? 'Yönetici' : 'Kullanıcı'}</div>
              <div className="small muted">{m.password_set === false
                ? (m.code_expires_at ? `Şifre belirlemedi · kod ${formatDateTime(m.code_expires_at)} tarihine kadar geçerli` : 'Şifre belirlemedi · kodun süresi doldu, yeni kod üretin')
                : m.last_login_at ? `Son giriş ${formatDateTime(m.last_login_at)}` : 'Henüz giriş yapmadı'}</div>
            </div>
            {!m.is_active ? <span className="badge">Erişim kapalı</span> : m.password_set === false ? <span className="badge warn">Kurulum bekliyor</span> : <span className="badge ok">Aktif</span>}
            {m.is_active && (
              <div className="row">
                <button className="btn small" disabled={reset.isPending}
                  onClick={() => { if (confirm(`${m.full_name} için şifre sıfırlansın mı? Mevcut şifresi ve açık oturumları geçersiz olur; yeni bir kod üretilir.`)) reset.mutate(m.user_id); }}>
                  {m.password_set === false ? 'Yeni kod üret' : 'Şifreyi sıfırla'}
                </button>
                {m.user_id !== me.user.id && (
                  <button className="btn small" disabled={role.isPending}
                    onClick={() => role.mutate({ id: m.user_id, role: m.role === 'admin' ? 'member' : 'admin' })}>
                    {m.role === 'admin' ? 'Yöneticiliği al' : 'Yönetici yap'}
                  </button>
                )}
                {m.user_id !== me.user.id && (
                  <button className="btn small danger" onClick={() => { if (confirm(`${m.full_name} için erişim kapatılsın mı? Tüm oturumları sonlanır; geçmiş kayıtları korunur.`)) deact.mutate(m.user_id); }}>Erişimi kapat</button>
                )}
              </div>
            )}
            {!m.is_active && <button className="btn small" onClick={() => react.mutate(m.user_id)}>Erişimi yeniden aç</button>}
          </div></li>
        ))}
      </ul></div>
    </div>
  );
}

/** Üretilen kod yalnızca burada, bir kez görünür. */
function CodePanel({ code, onClose }: { code: SetupCode & { kind: 'new' | 'reset' }; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = `Fiş takip sistemi${code.kind === 'reset' ? ' şifre sıfırlama' : ''} kodunuz: ${code.setup_code}\n`
    + `${window.location.origin}/ilk-giris adresini açın, ${code.email} adresiniz ve bu kodla kendi şifrenizi belirleyin.`;
  return (
    <div className="card stack" style={{ borderColor: 'var(--ok)' }} role="status">
      <strong>{code.kind === 'new' ? `${code.full_name} tanımlandı` : `${code.full_name} için şifre sıfırlandı`}</strong>
      <div className="code-box" aria-label="Tek kullanımlık kod">{code.setup_code}</div>
      <div className="small">Kod {code.email} adresine gönderildi. E-posta ulaşmazsa bu kodu kullanıcıya siz iletin. Kod {formatDateTime(code.code_expires_at)} tarihine kadar geçerli ve tek kullanımlık. Bu pencere kapanınca kod tekrar gösterilmez.</div>
      <div className="row">
        <button className="btn small" onClick={() => { void navigator.clipboard?.writeText(text).then(() => setCopied(true)).catch(() => setCopied(false)); }}>{copied ? 'Kopyalandı' : 'Mesajı kopyala'}</button>
        <span className="spacer" />
        <button className="btn small" onClick={onClose}>Kapat</button>
      </div>
    </div>
  );
}

/** Gider kalemleri: sistem fişlerden otomatik oluşturur; yönetici benzer kalemleri birleştirir veya adını düzeltir. */
function CategoriesAdmin() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['categories-admin'], queryFn: ep.categories });
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['categories-admin'] }), qc.invalidateQueries({ queryKey: ['stats'] })]);
  const merge = useMutation({ mutationFn: (v: { id: string; into: string }) => ep.mergeCategory(v.id, v.into), onSuccess: refresh });
  const unmerge = useMutation({ mutationFn: ep.unmergeCategory, onSuccess: refresh });
  const rename = useMutation({ mutationFn: (v: { id: string; name: string }) => ep.renameCategory(v.id, v.name), onSuccess: refresh });
  const [target, setTarget] = useState<Record<string, string>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  if (q.error) return <ErrorBox error={q.error} />;
  if (!q.data) return <Loading />;
  if (!q.data.length) return <Empty>Henüz gider kalemi yok. Fiş eklendikçe kategorilerden kendiliğinden oluşur.</Empty>;
  return (
    <div className="stack">
      <p className="small muted" style={{ margin: 0 }}>Sistem fişlerdeki kategorilerden gider kalemlerini kendisi oluşturur. Benzer yazımlar ("Yemek", "yemekler") ve sık eşanlamlılar ("Benzin", "Akaryakıt" → Yakıt) zaten tek kalemde toplanır. Farklı yazılmış iki kalem aynı gideri anlatıyorsa burada birleştirin. Fişlerdeki yazım değişmez; yalnızca istatistik tek kalemde toplanır.</p>
      <ErrorBox error={merge.error ?? unmerge.error ?? rename.error} />
      <div className="card"><ul className="list">
        {q.data.map((c) => (
          <li key={c.id}><div className="item" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
              <div className="title">{c.name} <span className="small muted">· {c.receipt_count} fiş</span></div>
              {c.merged.length > 0 && (
                <div className="small muted">Bu kaleme birleştirilenler:{' '}
                  {c.merged.map((m) => (
                    <span key={m.id} style={{ whiteSpace: 'nowrap' }}>{m.name} ({m.receipt_count}){' '}
                      <button className="btn small" style={{ minHeight: 26, padding: '2px 6px' }} onClick={() => unmerge.mutate(m.id)}>Ayır</button>{' '}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="row" style={{ flex: '1 1 260px' }}>
              <select aria-label={`${c.name} kalemini birleştir`} value={target[c.id] ?? ''} onChange={(e) => setTarget({ ...target, [c.id]: e.target.value })} style={{ flex: 1, minWidth: 0 }}>
                <option value="">Şu kaleme birleştir…</option>
                {q.data.filter((o) => o.id !== c.id).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <button className="btn small" disabled={!target[c.id] || merge.isPending}
                onClick={() => { const into = q.data.find((o) => o.id === target[c.id]); if (into && confirm(`"${c.name}" kalemi "${into.name}" altında toplansın mı? Fişler değişmez.`)) merge.mutate({ id: c.id, into: into.id }); }}>Birleştir</button>
            </div>
            <div className="row" style={{ flex: '1 1 260px' }}>
              <input aria-label={`${c.name} yeni adı`} placeholder="Yeni ad" value={names[c.id] ?? ''} onChange={(e) => setNames({ ...names, [c.id]: e.target.value })} style={{ flex: 1, minWidth: 0 }} maxLength={80} />
              <button className="btn small" disabled={!(names[c.id] ?? '').trim() || rename.isPending}
                onClick={() => rename.mutate({ id: c.id, name: names[c.id] ?? '' }, { onSuccess: () => setNames({ ...names, [c.id]: '' }) })}>Adını değiştir</button>
            </div>
          </div></li>
        ))}
      </ul></div>
    </div>
  );
}

function Notifications() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['notifications'], queryFn: ep.notifications });
  const read = useMutation({ mutationFn: ep.readNotification, onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }) });
  const text = (t: string, p: Record<string, string>) => ({
    'period.close_prep': `${p.year_month ?? ''} ayı bugün bitiyor; eksik fiş yoksa ayı kapatın.`,
    'period.close_due': `${p.year_month ?? ''} ayı henüz kapatılmadı.`,
    'duplicate.decided': 'Mükerrer inceleme kararı verildi.',
  }[t] ?? t);
  if (!q.data) return <Loading />;
  return (
    <div className="card"><ul className="list">
      {q.data.map((n) => (
        <li key={n.id}><div className="item">
          <div style={{ flex: 1 }}><div className={n.read_at ? '' : 'title'}>{n.body || text(n.type, n.payload)}</div><div className="small muted">{formatDateTime(n.created_at)}</div></div>
          {!n.read_at && <button className="btn small" onClick={() => read.mutate(n.id)}>Okundu</button>}
        </div></li>
      ))}
      {!q.data.length && <li className="empty">Bildirim yok.</li>}
    </ul></div>
  );
}
